import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getNetworkErrorMessage,
  isLikelyNetworkError,
} from "../utils/networkErrors";

const BANNER_AUTO_HIDE_MS = 8000;
const DEFAULT_SLOW_REQUEST_MS = 7000;
const REDUCED_SLOW_REQUEST_MS = 3500;
const NETWORK_ISSUE_TIMEOUT_MS = 30000;

const getInitialOnlineState = () => {
  if (typeof navigator === "undefined") {
    return true;
  }

  return navigator.onLine;
};

const getConnectionDetails = () => {
  if (typeof navigator === "undefined") {
    return {
      isConstrained: false,
      effectiveType: "",
      saveData: false,
    };
  }

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  const effectiveType = connection?.effectiveType || "";
  const saveData = Boolean(connection?.saveData);

  return {
    isConstrained:
      saveData ||
      effectiveType === "slow-2g" ||
      effectiveType === "2g" ||
      effectiveType === "3g",
    effectiveType,
    saveData,
  };
};

const GlobalNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(getInitialOnlineState);
  const initialIsOffline = !getInitialOnlineState();
  const [pendingRequests, setPendingRequests] = useState(0);
  const [showSlowLoading, setShowSlowLoading] = useState(false);
  const [showTimedOutState, setShowTimedOutState] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(getConnectionDetails);
  const [errorState, setErrorState] = useState({
    visible: initialIsOffline,
    message: getNetworkErrorMessage({ isOffline: initialIsOffline }),
    isOffline: initialIsOffline,
  });

  const message = useMemo(
    () =>
      errorState.message ||
      getNetworkErrorMessage({
        isOffline: errorState.isOffline,
        isTimedOut: errorState.isTimedOut,
      }),
    [errorState.isOffline, errorState.isTimedOut, errorState.message]
  );

  const slowLoadingMessage = useMemo(() => {
    if (connectionInfo.saveData) {
      return "Loading is taking longer because data-saving mode is enabled. We are still trying to fetch the page.";
    }

    if (connectionInfo.effectiveType) {
      return `Loading is taking longer than usual on your connection. We are still trying to fetch the latest data.`;
    }

    return "Loading is taking longer than usual because of the current network connection. We are still trying to fetch the latest data.";
  }, [connectionInfo.effectiveType, connectionInfo.saveData]);

  useEffect(() => {
    const showNetworkError = ({
      isOffline: offline = !navigator.onLine,
      isTimedOut = false,
    } = {}) => {
      setShowSlowLoading(false);
      setShowTimedOutState(isTimedOut);
      setErrorState({
        visible: true,
        message: getNetworkErrorMessage({
          isOffline: offline,
          isTimedOut,
        }),
        isOffline: offline,
        isTimedOut,
      });
    };

    const clearNetworkError = () => {
      setErrorState((current) => ({
        ...current,
        visible: false,
      }));
      setShowTimedOutState(false);
    };

    const handleOnline = () => {
      setIsOnline(true);
      clearNetworkError();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowSlowLoading(false);
      showNetworkError({ isOffline: true });
    };

    const handleConnectionChange = () => {
      setConnectionInfo(getConnectionDetails());
    };

    const startRequest = () => {
      setPendingRequests((current) => current + 1);
    };

    const finishRequest = () => {
      setPendingRequests((current) => Math.max(0, current - 1));
    };

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      startRequest();

      try {
        return await originalFetch(...args);
      } catch (error) {
        if (isLikelyNetworkError(error) && !navigator.onLine) {
          showNetworkError({ isOffline: true });
        }

        throw error;
      } finally {
        finishRequest();
      }
    };

    const requestInterceptorId = axios.interceptors.request.use(
      (config) => {
        startRequest();
        return config;
      },
      (error) => {
        finishRequest();
        return Promise.reject(error);
      }
    );

    const interceptorId = axios.interceptors.response.use(
      (response) => {
        finishRequest();
        return response;
      },
      (error) => {
        finishRequest();

        if (isLikelyNetworkError(error) && !navigator.onLine) {
          showNetworkError({ isOffline: true });
        }

        return Promise.reject(error);
      }
    );

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("connectionchange", handleConnectionChange);
    navigator.connection?.addEventListener?.("change", handleConnectionChange);
    navigator.mozConnection?.addEventListener?.(
      "change",
      handleConnectionChange
    );
    navigator.webkitConnection?.addEventListener?.(
      "change",
      handleConnectionChange
    );

    return () => {
      window.fetch = originalFetch;
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(interceptorId);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("connectionchange", handleConnectionChange);
      navigator.connection?.removeEventListener?.(
        "change",
        handleConnectionChange
      );
      navigator.mozConnection?.removeEventListener?.(
        "change",
        handleConnectionChange
      );
      navigator.webkitConnection?.removeEventListener?.(
        "change",
        handleConnectionChange
      );
    };
  }, []);

  useEffect(() => {
    if (!errorState.visible || !isOnline || errorState.isTimedOut) {
      return undefined;
    }

    const hideTimer = window.setTimeout(() => {
      setErrorState((current) => ({
        ...current,
        visible: false,
      }));
    }, BANNER_AUTO_HIDE_MS);

    return () => {
      window.clearTimeout(hideTimer);
    };
  }, [errorState.isTimedOut, errorState.visible, isOnline]);

  // useEffect(() => {
  //   if (
  //     !isOnline ||
  //     pendingRequests === 0 ||
  //     errorState.visible ||
  //     showTimedOutState
  //   ) {
  //     setShowSlowLoading(false);
  //     return undefined;
  //   }

  //   const slowRequestThreshold = connectionInfo.isConstrained
  //     ? REDUCED_SLOW_REQUEST_MS
  //     : DEFAULT_SLOW_REQUEST_MS;

  //   const slowTimer = window.setTimeout(() => {
  //     setShowSlowLoading(true);
  //   }, slowRequestThreshold);

  //   return () => {
  //     window.clearTimeout(slowTimer);
  //   };
  // }, [
  //   connectionInfo.isConstrained,
  //   errorState.visible,
  //   isOnline,
  //   pendingRequests,
  //   showTimedOutState,
  // ]);

  // useEffect(() => {
  //   if (!isOnline || pendingRequests === 0 || errorState.visible) {
  //     setShowTimedOutState(false);
  //     return undefined;
  //   }

  //   const timeoutTimer = window.setTimeout(() => {
  //     setShowSlowLoading(false);
  //     setShowTimedOutState(true);
  //     setErrorState({
  //       visible: true,
  //       message: getNetworkErrorMessage({ isTimedOut: true }),
  //       isOffline: false,
  //       isTimedOut: true,
  //     });
  //   }, NETWORK_ISSUE_TIMEOUT_MS);

  //   return () => {
  //     window.clearTimeout(timeoutTimer);
  //   };
  // }, [errorState.visible, isOnline, pendingRequests]);

  useEffect(() => {
    if (pendingRequests === 0) {
      setShowSlowLoading(false);
      setShowTimedOutState(false);
      setErrorState((current) =>
        current.isOffline
          ? current
          : {
              ...current,
              visible: false,
              isTimedOut: false,
            }
      );
    }
  }, [pendingRequests]);

  if (!errorState.visible && !showSlowLoading) {
    return null;
  }

  return (
    <>
      {showSlowLoading && !errorState.visible ? (
        <div
          className="global-network-overlay"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="global-network-overlay__content">
            <div
              className="global-network-overlay__spinner"
              aria-hidden="true"
            />
            <p className="global-network-overlay__title">Still loading</p>
            <p className="global-network-overlay__message">
              {slowLoadingMessage}
            </p>
            <div className="global-network-status__actions">
              <button
                type="button"
                className="global-network-status__button global-network-status__button--secondary"
                onClick={() => setShowSlowLoading(false)}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="global-network-status__button global-network-status__button--primary"
                onClick={() => window.location.reload()}
                disabled={!isOnline}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {errorState.visible ? (
        <div
          className="global-network-status"
          role={errorState.isOffline ? "alert" : "status"}
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="global-network-status__content">
            <div>
              <p className="global-network-status__title">
                {errorState.isOffline
                  ? "Connection lost"
                  : "Network issue while loading"}
              </p>
              <p className="global-network-status__message">{message}</p>
            </div>

            <div className="global-network-status__actions">
              <button
                type="button"
                className="global-network-status__button global-network-status__button--secondary"
                onClick={() =>
                  setErrorState((current) => ({
                    ...current,
                    visible: false,
                  }))
                }
              >
                Dismiss
              </button>
              <button
                type="button"
                className="global-network-status__button global-network-status__button--primary"
                onClick={() => window.location.reload()}
                disabled={!isOnline}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default GlobalNetworkStatus;
